import { queryOptions, useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api-client";
import { hasAccessToken } from "@/lib/auth";

import type { QueryConfig } from "@/lib/react-query";
import type { Attachment } from "@/types/attachment";
import type { List } from "@/types/entity";

export const getCarImages = async (carId: string) => {
  const response = (await api.get(`/carburate/files/cars/images/${carId}/`, {
    params: {
      page: 1,
      page_size: 999999,
    },
  })) as List<Attachment>;

  // This part fetches each image blob and creates an object URL for it for display purposes - may not be optimal for large number of images
  const resultsWithBlobs = await Promise.all(
    response.results.map(async (attachment) => {
      const url = `/carburate/files/cars/images/${carId}/id/${attachment.id}/`;

      // here is where the image blob is fetched with auth token
      const blob = await api.get(url, {
        responseType: "blob",
      });

      const objectUrl = URL.createObjectURL(blob as unknown as Blob);

      return {
        ...attachment,
        path: objectUrl,
      };
    })
  );

  return {
    ...response,
    results: resultsWithBlobs,
  };
};

export const getCarImagesQueryOptions = (carId: string) => {
  return queryOptions({
    queryKey: ["cars", carId, "images"],
    queryFn: () => getCarImages(carId),
  });
};

type UseCarImagesOptions = {
  carId: string;
  queryConfig?: QueryConfig<typeof getCarImages>;
};

export const useCarImages = ({ carId, queryConfig }: UseCarImagesOptions) => {
  return useQuery({
    enabled: hasAccessToken(),
    ...getCarImagesQueryOptions(carId),
    ...queryConfig,
  });
};
